import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClientInfo } from '../../types';
import ClientDropdown from './ClientDropdown';
import CreateClientForm, { CreateClientFormData } from '../forms/CreateClientForm';
import EditClientForm, { EditClientFormData } from '../forms/EditClientForm';
import {
  ArrowsRightLeftIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  PhoneIcon,
  TrashIcon,
  UserIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

interface ClientInfoBoxProps {
  clientInfo: ClientInfo | null | undefined;
  onAssociate?: (clientId: string) => Promise<void>;
  onCreate?: (data: CreateClientFormData) => Promise<{ clientId: string; warning?: string }>;
  onEdit?: (clientId: string, data: EditClientFormData) => Promise<void>;
  onRemove?: () => Promise<void>;
  isLoading?: boolean;
  className?: string;
  readOnly?: boolean;
}

/**
 * Client association box for leads/tickets with empty state and edit mode.
 * Feature: 018-edit-client-association
 * 
 * US1: Display empty state with "Associate Client" action
 * US1: Display client info with edit/remove actions
 * US2: Support "Create New Client" workflow with duplicate detection
 * US4: Support inline editing with dropdown
 */
export default function ClientInfoBox({
  clientInfo,
  onAssociate,
  onCreate,
  onEdit,
  onRemove,
  isLoading = false,
  className = '',
  readOnly = false,
}: ClientInfoBoxProps) {
  const [mode, setMode] = useState<'view' | 'selecting' | 'creating' | 'editing'>('view');
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSelect = async (clientId: string) => {
    if (!onAssociate) return;
    try {
      await onAssociate(clientId);
      setMode('view');
    } catch (error) {
      // Error handled by parent component via toast
      console.error('Failed to associate client:', error);
    }
  };

  const handleCreate = async (data: CreateClientFormData) => {
    if (!onCreate || !onAssociate) return;

    try {
      const result = await onCreate(data);
      
      // If duplicate detected, show warning
      if (result.warning) {
        setDuplicateWarning(result.warning);
      }
      
      // Auto-associate with the client (new or existing)
      await onAssociate(result.clientId);
      setMode('view');
      setDuplicateWarning(null);
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const handleEdit = async (data: EditClientFormData) => {
    if (!onEdit || !clientInfo) return;

    try {
      await onEdit(clientInfo.accountId, data);
      setMode('view');
    } catch (error) {
      console.error('Failed to edit client:', error);
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;
    
    if (confirm('Remove account association? This will not delete the account record.')) {
      try {
        await onRemove();
      } catch (error) {
        console.error('Failed to remove client:', error);
      }
    }
  };

  // Empty state - no client associated
  if (!clientInfo && mode === 'view') {
    if (readOnly) {
      return (
        <div className={`bg-white border border-gray-200 rounded-xl shadow-sm p-6 ${className}`}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
            Account Information
          </h3>
          <p className="text-sm text-gray-500">No account associated.</p>
        </div>
      );
    }

    return (
      <div className={`bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No account associated</h3>
          <p className="mt-1 text-sm text-gray-500">Link this to an existing account or create a new one</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              type="button"
              onClick={() => setMode('selecting')}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Select Existing
            </button>
            {onCreate && (
              <button
                type="button"
                onClick={() => setMode('creating')}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Create New Account
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Create mode - show create form
  if (mode === 'creating') {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-6 ${className}`}>
        <CreateClientForm
          onSubmit={handleCreate}
          onCancel={() => {
            setMode('view');
            setDuplicateWarning(null);
          }}
          isLoading={isLoading}
          duplicateWarning={duplicateWarning}
        />
      </div>
    );
  }

  // Edit mode - show edit form
  if (mode === 'editing' && clientInfo) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-6 ${className}`}>
        <EditClientForm
          clientInfo={clientInfo}
          onSubmit={handleEdit}
          onCancel={() => setMode('view')}
          isLoading={isLoading}
        />
      </div>
    );
  }

  // Select mode - show dropdown
  if (mode === 'selecting') {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Select Account
          </h3>
          <div className="flex gap-2">
            {onCreate && (
              <button
                type="button"
                onClick={() => setMode('creating')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Create New
              </button>
            )}
            <button
              type="button"
              onClick={() => setMode('view')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
        <ClientDropdown
          onSelect={handleSelect}
          placeholder="Search accounts by name..."
          autoFocus
        />
      </div>
    );
  }

  // Display mode - show client info with edit/remove actions
  return (
    <div className={`bg-white border border-gray-200 rounded-xl shadow-sm p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Account Information
          </h3>
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="rounded-md border border-gray-200 p-1 text-gray-500 transition hover:bg-gray-50"
            title={isExpanded ? 'Collapse account details' : 'Expand account details'}
            aria-label={isExpanded ? 'Collapse account details' : 'Expand account details'}
            aria-expanded={isExpanded}
          >
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              title={isExpanded ? 'Collapse account details' : 'Expand account details'}
            />
          </button>
        </div>

        <div className="flex gap-2">
            {!readOnly && onEdit && (
            <button
              type="button"
              onClick={() => setMode('editing')}
              disabled={isLoading}
              className="rounded-md border border-gray-200 p-2 text-blue-600 transition hover:bg-blue-50 disabled:opacity-50"
              title="Edit"
              aria-label="Edit"
            >
              <PencilSquareIcon className="h-4 w-4" title="Edit" />
            </button>
          )}
          {!readOnly && onAssociate && (
            <button
              type="button"
              onClick={() => setMode('selecting')}
              disabled={isLoading}
              className="rounded-md border border-gray-200 p-2 text-blue-600 transition hover:bg-blue-50 disabled:opacity-50"
              title="Change Account"
              aria-label="Change Account"
            >
              <ArrowsRightLeftIcon className="h-4 w-4" title="Change Account" />
            </button>
          )}
          {!readOnly && onRemove && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={isLoading}
              className="rounded-md border border-gray-200 p-2 text-red-600 transition hover:bg-red-50 disabled:opacity-50"
              title="Remove Account"
              aria-label="Remove Account"
            >
              <TrashIcon className="h-4 w-4" title="Remove Account" />
            </button>
          )}
        </div>
      </div>

      {!isExpanded && (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-gray-50 p-3">
            <dt className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
              <BuildingOffice2Icon className="h-4 w-4" title="Company" /> Company
            </dt>
            <dd className="text-sm font-semibold text-gray-900">
              {clientInfo!.companyName ? (
                <Link
                  to={`/clients/${clientInfo!.accountId}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {clientInfo!.companyName}
                </Link>
              ) : (
                <span className="text-gray-400 italic">Not provided</span>
              )}
            </dd>
          </div>

          <div className="rounded-lg bg-gray-50 p-3">
            <dt className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
              <UserIcon className="h-4 w-4" title="Contact" /> Contact
            </dt>
            <dd className="text-sm text-gray-900">
              {clientInfo!.contactName || <span className="text-gray-400 italic">Not provided</span>}
            </dd>
          </div>

          <div className="rounded-lg bg-gray-50 p-3">
            <dt className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
              <PhoneIcon className="h-4 w-4" title="Phone" /> Phone
            </dt>
            <dd className="text-sm text-gray-900">
              {clientInfo!.phone ? (
                <a href={`tel:${clientInfo!.phone}`} className="hover:text-blue-600">
                  {clientInfo!.phone}
                </a>
              ) : (
                <span className="text-gray-400 italic">Not provided</span>
              )}
            </dd>
          </div>

          <div className="rounded-lg bg-gray-50 p-3">
            <dt className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
              <EnvelopeIcon className="h-4 w-4" title="Email" /> Email
            </dt>
            <dd className="text-sm text-gray-900">
              {clientInfo!.email ? (
                <a href={`mailto:${clientInfo!.email}`} className="hover:text-blue-600 break-all">
                  {clientInfo!.email}
                </a>
              ) : (
                <span className="text-gray-400 italic">Not provided</span>
              )}
            </dd>
          </div>
        </dl>
      )}

      {isExpanded && <div className="space-y-3">
        {/* Company Name - Clickable link to account profile */}
        {clientInfo!.companyName && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Company</dt>
            <dd className="mt-1">
              <Link
                to={`/clients/${clientInfo!.accountId}`}
                className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline"
              >
                {clientInfo!.companyName}
              </Link>
            </dd>
          </div>
        )}

        {/* Contact Name */}
        <div>
          <dt className="text-xs font-medium text-gray-500">Contact</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {clientInfo!.contactName || <span className="text-gray-400 italic">Not provided</span>}
          </dd>
        </div>

        {/* Email - mailto: link */}
        {clientInfo!.email && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Email</dt>
            <dd className="mt-1">
              <a
                href={`mailto:${clientInfo!.email}`}
                className="text-sm text-gray-900 hover:text-blue-600"
              >
                {clientInfo!.email}
              </a>
            </dd>
          </div>
        )}

        {/* Phone - tel: link */}
        {clientInfo!.phone && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Phone</dt>
            <dd className="mt-1">
              <a
                href={`tel:${clientInfo!.phone}`}
                className="text-sm text-gray-900 hover:text-blue-600"
              >
                {clientInfo!.phone}
              </a>
            </dd>
          </div>
        )}

        {/* Address - static text */}
        {clientInfo!.address && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Address</dt>
            <dd className="mt-1 text-sm text-gray-900">{clientInfo!.address}</dd>
          </div>
        )}

        {/* Status - Feature 019 */}
        <div>
          <dt className="text-xs font-medium text-gray-500">Status</dt>
          <dd className="mt-1">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                clientInfo!.status?.toUpperCase() === 'ACTIVE'
                  ? 'bg-brand-accent/15 text-brand-accent'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {clientInfo!.status?.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
            </span>
          </dd>
        </div>

        {/* Account Segment - Feature 019 */}
        {clientInfo!.clientSegment && (
          <div>
            <dt className="text-xs font-medium text-gray-500">Account Segment</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {clientInfo!.clientSegment === 'BeautyProClinics' && 'Beauty pro / Clinics'}
              {clientInfo!.clientSegment === 'DropshipperReseller' && 'Dropshipper / Reseller'}
              {clientInfo!.clientSegment === 'Corporate' && 'Corporate'}
              {clientInfo!.clientSegment === 'BrandOwner' && 'Brand owner'}
              {clientInfo!.clientSegment === 'InfluencerCreator' && 'Influencer / Creator'}
              {clientInfo!.clientSegment === 'Other' && 'Other'}
            </dd>
          </div>
        )}
      </div>}
    </div>
  );
}
